import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { FaProjectDiagram } from '@react-icons/all-files/fa/FaProjectDiagram';
import ProfaseModal from '../../commons/ProfaseModal';
import PageForm, { FormField, SelectOption } from '../../commons/PageForm';
import PageAlert, { usePageAlert } from '../../commons/PageAlert';
import { useAppDispatch, useAppSelector } from '../../store';
import { getEntity, updateEntity } from '../../manager/workflow-def.reducer';
import {
  normalizeWorkflowPeriodicidad,
  WORKFLOW_PERIODICIDAD_OPTIONS,
} from '../../shared/workflow-periodicidad';

const ACCENT = '#10b981';

interface WorkflowForm {
  id: string;
  nombre: string;
  periodicidad: string;
  periodo: string;
  porDefecto: string;
  executorClass: string;
  workflowClase: string;
}

const empty: WorkflowForm = {
  id: '',
  nombre: '',
  periodicidad: '',
  periodo: '',
  porDefecto: 'false',
  executorClass: '',
  workflowClase: '',
};

export default function WorkflowEditModal() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const entity        = useAppSelector(s => s.workflowDef.entity) as any;
  const loading       = useAppSelector(s => s.workflowDef.loading);
  const updating      = useAppSelector(s => s.workflowDef.updating);
  const updateSuccess = useAppSelector(s => s.workflowDef.updateSuccess);
  const errorMessage  = useAppSelector(s => s.workflowDef.errorMessage) as string | null;

  const [form, setForm]                 = useState<WorkflowForm>(empty);
  const [errors, setErrors]             = useState<Partial<Record<keyof WorkflowForm, string>>>({});
  const [claseOptions, setClaseOptions] = useState<SelectOption[]>([]);

  const { alert, showToast } = usePageAlert();
  const didSubmit = useRef(false);

  useEffect(() => {
    axios
      .get('api/profase-workflow-clases?page=0&size=999&sort=nombre,asc')
      .then(res => {
        const opts: SelectOption[] = (res.data as any[]).map(c => ({
          value: String(c.id),
          label: `${c.nombre}`,
        }));
        setClaseOptions(opts);
      })
      .catch(() => setClaseOptions([]));
  }, []);

  useEffect(() => {
    didSubmit.current = false;
    if (id) dispatch(getEntity(id));
  }, [id, dispatch]);

  useEffect(() => {
    if (entity?.id && String(entity.id) === String(id)) {
      setForm({
        id: String(entity.id),
        nombre: entity.nombre ?? '',
        periodicidad: normalizeWorkflowPeriodicidad(entity.periodicidad),
        periodo: entity.periodo != null ? String(entity.periodo) : '',
        porDefecto: String(entity.porDefecto ?? false),
        executorClass: entity.executorClass ?? '',
        workflowClase: entity.workflowClase?.id != null ? String(entity.workflowClase.id) : '',
      });
    }
  }, [entity, id]);

  useEffect(() => {
    if (errorMessage) {
      showToast('error', errorMessage);
    }
  }, [errorMessage, showToast]);

  useEffect(() => {
    if (updateSuccess && didSubmit.current) {
      didSubmit.current = false;
      navigate('/workflow');
    }
  }, [updateSuccess, navigate]);

  const fields = useMemo((): FormField<WorkflowForm>[] => [
    {
      name:     'id',
      label:    'ID',
      readOnly: true,
      editOnly: true,
    },
    {
      name:        'nombre',
      label:       'Nombre',
      required:    true,
      placeholder: 'Nombre del workflow',
    },
    {
      name:     'periodicidad',
      label:    'Periodicidad',
      type:     'select',
      options:  WORKFLOW_PERIODICIDAD_OPTIONS,
    },
    {
      name:        'periodo',
      label:       'Período',
      type:        'number',
      placeholder: 'Ej: 1, 7, 30…',
    },
    {
      name:        'executorClass',
      label:       'Executor Class',
      placeholder: 'com.example.MyExecutor',
    },
    {
      name:    'workflowClase',
      label:   'Clase Workflow',
      type:    'select',
      options: claseOptions,
    },
    {
      name:  'porDefecto',
      label: 'Por Defecto',
      type:  'checkbox',
    },
  ], [claseOptions]);

  const close = () => navigate('/workflow');

  const handleFieldChange = (name: keyof WorkflowForm, value: string) => {
    setForm(prev => ({ ...prev, [name]: value }));
    setErrors(prev => ({ ...prev, [name]: undefined }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const nextErrors: Partial<Record<keyof WorkflowForm, string>> = {};
    if (!form.nombre.trim()) nextErrors.nombre = 'El nombre es requerido';
    if (Object.keys(nextErrors).length) {
      setErrors(nextErrors);
      return;
    }

    const payload: any = {
      id: Number(form.id),
      ...entity,
      nombre: form.nombre.trim(),
      periodicidad: form.periodicidad || null,
      periodo: form.periodo ? Number(form.periodo) : null,
      porDefecto: form.porDefecto === 'true',
      executorClass: form.executorClass || null,
      ...(form.workflowClase ? { workflowClase: { id: Number(form.workflowClase) } } : {}),
    };

    didSubmit.current = true;
    dispatch(updateEntity(payload));
  };

  return (
    <ProfaseModal
      open
      titleId="workflow-edit-modal-title"
      size="wide"
      scroll
    >
      <div style={s.header}>
        <h2 id="workflow-edit-modal-title" className="profase-modal-title m-0">
          <FaProjectDiagram style={{ marginRight: 8, verticalAlign: '-2px' }} />
          Editar Workflow
        </h2>
        <button
          type="button"
          className="profase-form-btn-secondary btn-sm"
          onClick={close}
          disabled={updating}
        >
          Cerrar
        </button>
      </div>

      <p className="profase-modal-description">
        Modifique los datos del workflow.
      </p>

      {alert && <PageAlert alert={alert} />}

      <PageForm
        embedded
        fields={fields}
        values={form}
        errors={errors}
        isNew={false}
        saving={updating || loading}
        labelInline
        onFieldChange={handleFieldChange}
        onSubmit={handleSubmit}
        onCancel={close}
        accentColor={ACCENT}
        submitLabel="Guardar cambios"
        cancelLabel="Cancelar"
      />
    </ProfaseModal>
  );
}

const s: Record<string, React.CSSProperties> = {
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    marginBottom: 8,
    flexWrap: 'wrap',
  },
};
